import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const StaffContext = createContext(null);

export function StaffProvider({ children }) {
  const [staff, setStaff] = useState(null); // null=checking, false=not, obj=logged

  const refresh = useCallback(async () => {
    // Ne tente /staff/me QUE sur les routes du portail staff. Évite le bruit
    // 401 dans la console quand un tenant navigue dans son app normale.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (!path.startsWith("/staff")) {
      setStaff(false);
      return;
    }
    try {
      const { data } = await api.get("/staff/me");
      setStaff(data.staff);
    } catch {
      setStaff(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const beginLogin = async (email, password) => {
    try {
      const { data } = await api.post("/staff/login", { email, password });
      // Dev-mode bypass: STAFF_2FA_REQUIRED=false → server returns the
      // access token straight away, no challenge to solve.
      if (data.twofa_bypassed) {
        await refresh();
        return { ok: true, bypassed: true };
      }
      return { ok: true, challenge_token: data.challenge_token };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const verify2fa = async (challenge_token, code) => {
    try {
      await api.post("/staff/verify-2fa", { challenge_token, code });
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/staff/logout");
    } catch {
      /* ignore */
    }
    setStaff(false);
  };

  return (
    <StaffContext.Provider value={{ staff, refresh, beginLogin, verify2fa, logout }}>
      {children}
    </StaffContext.Provider>
  );
}

export function useStaff() {
  const ctx = useContext(StaffContext);
  if (!ctx) throw new Error("useStaff must be used inside StaffProvider");
  return ctx;
}
